// src/modules/PublicRegistration/PublicRegistrationForm.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  PhotoIcon,
  CameraIcon,
  XMarkIcon,
  PlusCircleIcon,
  TrashIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { FaInstagram, FaFacebookF, FaTiktok, FaWhatsapp, FaPinterest } from 'react-icons/fa';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import BarrioSelect from '@/components/common/BarrioSelect/BarrioSelect';
import { HABEAS_DATA_TEXTO } from '@/constants/legalTexts';
import PublicCapacitacionesSelector from './components/PublicCapacitacionesSelector';
import { saveDraft, loadDraft, clearDraft, stripFotosNegocios } from '@/utils/formDraft';
import styles from './PublicRegistrationForm.module.css';

const phoneRegex = /^[0-9]{10}$/;
const sanitizePhoneInput = (value) => value.replace(/\D/g, '').slice(0, 10);
const VENTAS_OPCIONES = [
  'Menos de $500.000',
  '$500.000 - $1.000.000',
  '$1.000.001 - $3.000.000',
  '$3.000.001 - $6.000.000',
  'Más de $6.000.000',
];
const EMPLEOS_OPCIONES = ['Solo el mío', '1', '2', '3-5', '6-10', '11-20', '20 o más'];
const ANIOS_OPCIONES = ['Menos de 1 año', '1-2 años', '3-5 años', '6-10 años', 'Más de 10 años'];
const FORMAL_OPCIONES = ['RUT', 'Cámara de Comercio', 'Ambas', 'Ninguno'];
const TIPO_LOCAL_OPCIONES = ['Casa', 'Local comercial', 'Ambulante', 'Estacionario'];
const TIPOS_DOCUMENTO = ['CC', 'CE', 'PPT', 'Pasaporte', 'NUIP'];
const MEDIOS_PAGO = [
  ['efectivo', 'Efectivo', BanknotesIcon],
  ['transferencia', 'Transferencia', ArrowPathIcon],
  ['tarjeta', 'Tarjeta', CreditCardIcon],
];

const APOYO_OPCIONES = [
  'Recibi capacitación en alianza con el SENA',
  'Recibi capital semilla en especie',
  'Participe en ferias',
  'Recibi capacitación (cursos, talleres, asistencia técnica)',
  'No he recibido apoyo',
];

// La fecha de expedicion del documento no puede ser futura (máximo hoy)
const getMaxFechaExpedicion = () => {
  const hoy = new Date();
  return hoy.toISOString().split('T')[0];
};

const InstagramIcon = ({ width = 20, className = '' }) => (
  <FaInstagram width={width} className={className} />
);

const FacebookIcon = ({ width = 20, className = '' }) => (
  <FaFacebookF width={width} className={className} />
);

const TikTokIcon = ({ width = 20, className = '' }) => (
  <FaTiktok width={width} className={className} />
);

const WhatsAppIcon = ({ width = 20, className = '' }) => (
  <FaWhatsapp width={width} className={className} />
);

const PinterestIcon = ({ width = 20, className = '' }) => (
  <FaPinterest width={width} className={className} />
);

// Claves validas de negocios.redes_sociales -- incluye 'web' (legado, ya no
// se muestra en el formulario) y 'otro' (nombre personalizado) para que
// normalizeRedesSociales no las descarte al editar un negocio existente.
const REDES_SOCIALES = [
  ['instagram', 'Instagram', 'https://instagram.com/...', InstagramIcon],
  ['facebook', 'Facebook', 'https://facebook.com/...', FacebookIcon],
  ['tiktok', 'TikTok', 'https://tiktok.com/@...', TikTokIcon],
  ['whatsapp', 'WhatsApp Business', 'https://wa.me/57...', WhatsAppIcon],
  ['pinterest', 'Pinterest', 'https://pinterest.com/...', PinterestIcon],
  ['marketplace', 'Marketplace', 'https://marketplace.com/...', ShoppingBagIcon],
  ['web', 'Sitio web', 'https://...', GlobeAltIcon],
  ['otro', 'Otro', '', GlobeAltIcon],
];

// Mapea el nombre EXACTO del catalogo (catalogos_redes_sociales) a la clave
// bajo la que se guarda su cuenta/enlace en negocios.redes_sociales.
const CLAVE_RED_POR_NOMBRE = {
  Facebook: 'facebook',
  Instagram: 'instagram',
  WhatsApp: 'whatsapp',
  TikTok: 'tiktok',
  Pinterest: 'pinterest',
  Marketplace: 'marketplace',
};
const ICONO_POR_CLAVE = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  whatsapp: WhatsAppIcon,
  tiktok: TikTokIcon,
  pinterest: PinterestIcon,
  marketplace: ShoppingBagIcon,
};
const PLACEHOLDER_RED = {
  facebook: 'https://facebook.com/...',
  instagram: 'https://instagram.com/...',
  whatsapp: 'https://wa.me/57...',
  tiktok: 'https://tiktok.com/@...',
  pinterest: 'https://pinterest.com/...',
  marketplace: 'https://marketplace.com/...',
};
const NOMBRES_CATALOGO = Object.keys(CLAVE_RED_POR_NOMBRE);

const normalizeMediosPago = (medios = {}) =>
  MEDIOS_PAGO.reduce((acc, [key]) => {
    if (medios?.[key]) acc[key] = true;
    return acc;
  }, {});

const normalizeRedesSociales = (redes = {}) =>
  REDES_SOCIALES.reduce((acc, [key]) => {
    if (Object.prototype.hasOwnProperty.call(redes || {}, key)) {
      acc[key] = String(redes[key] || '').trim();
    }
    return acc;
  }, {});

const step1Schema = z.object({
  nombres: z.string().min(2, 'Nombre requerido'),
  apellidos: z.string().min(2, 'Apellido requerido'),
  tipo_documento: z.string().min(1, 'Seleccione tipo'),
  numero_documento: z.string().min(5, 'Mínimo 5 caracteres').max(15, 'Máximo 15 caracteres'),
  fecha_expedicion_documento: z
    .string()
    .optional()
    .refine((val) => !val || val <= getMaxFechaExpedicion(), {
      message: 'La fecha de expedición no puede ser una fecha futura',
    }),
  sexo: z.enum(['M', 'F', 'Otro'], { errorMap: () => ({ message: 'Seleccione sexo' }) }),
  edad: z.coerce.number().min(18, 'Debe tener al menos 18 años').max(80, 'Edad máxima 80 años'),
  poblaciones: z.array(z.string()).min(1, 'Seleccione al menos un tipo de población'),
  nivel_educativo: z.string().min(1, 'Seleccione nivel educativo'),
  personas_a_cargo: z.string().min(1, 'Seleccione cuantas personas dependen de usted'),
});

const step2Schema = z.object({
  telefono: z.string().regex(phoneRegex, 'Debe tener exactamente 10 dígitos'),
  tiene_whatsapp: z.string().optional(),
  correo: z.string().email('Correo inválido').optional().or(z.literal('')),
  barrio: z.string().min(1, 'Seleccione un barrio'),
});

const step6Schema = z.object({
  habeas_data: z.literal(true, {
    errorMap: () => ({ message: 'Debe aceptar el tratamiento de datos' }),
  }),
});

function NegocioForm({
  index,
  negocio,
  sectoresEconomicos,
  tiposNegocio,
  redesSocialesCatalogo,
  canRemove,
  onChange,
  onRemove,
  onFotoAdd,
  onFotoRemove,
}) {
  const esFuncionando = negocio.negocio_funcionando === 'SI';
  const set = (field, value) => onChange(index, field, value);

  // Filtra el tipo de negocio segun el sector economico elegido. Si aun no
  // hay sector seleccionado, muestra todas las opciones. Los tipos sin
  // sector asignado (ej. "Otro") se muestran siempre como comodin.
  const sectorSeleccionado = sectoresEconomicos.find(
    (s) => (typeof s === 'string' ? s : s.nombre) === negocio.sector_economico
  );
  const tiposNegocioFiltrados = !negocio.sector_economico
    ? tiposNegocio
    : tiposNegocio.filter((t) => {
        if (typeof t === 'string') return true;
        return !t.id_sector || t.id_sector === sectorSeleccionado?.id;
      });

  const handleRedSocialChange = (red, value) => {
    const redes = { ...negocio.redes_sociales, [red]: value };
    onChange(index, 'redes_sociales', redes);
  };
  const handleMedioPagoChange = (medio, checked) => {
    const medios = { ...negocio.medios_pago };
    if (checked) medios[medio] = true;
    else delete medios[medio];
    onChange(index, 'medios_pago', medios);
  };
  // Un solo checkbox por red: marca/desmarca en redes_comercializacion Y crea/
  // borra el slot de cuenta en redes_sociales (clave segun CLAVE_RED_POR_NOMBRE).
  const handleComercializaToggle = (nombreRed, checked) => {
    const actuales = negocio.redes_comercializacion || [];
    const siguientes = checked
      ? [...actuales, nombreRed]
      : actuales.filter((r) => r !== nombreRed);
    onChange(index, 'redes_comercializacion', siguientes);
    const clave = CLAVE_RED_POR_NOMBRE[nombreRed];
    if (clave) {
      const redes = { ...negocio.redes_sociales };
      if (checked) redes[clave] = redes[clave] ?? '';
      else delete redes[clave];
      onChange(index, 'redes_sociales', redes);
    }
  };

  // Nombre personalizado ("Otro"): cualquier entrada de redes_comercializacion
  // que no sea uno de los nombres fijos del catalogo.
  const otroNombre = (negocio.redes_comercializacion || []).find((n) => !NOMBRES_CATALOGO.includes(n)) || '';
  const handleOtroNombreChange = (nuevoNombre) => {
    const actuales = (negocio.redes_comercializacion || []).filter((n) => NOMBRES_CATALOGO.includes(n));
    const siguientes = nuevoNombre.trim() ? [...actuales, nuevoNombre.trim()] : actuales;
    onChange(index, 'redes_comercializacion', siguientes);
    if (!nuevoNombre.trim()) {
      const redes = { ...negocio.redes_sociales };
      delete redes.otro;
      onChange(index, 'redes_sociales', redes);
    }
  };

  return (
    <div className={styles.negocioCard}>
      <div className={styles.negocioHeader}>
        <span className={styles.negocioLabel}>{index === 0 ? 'Negocio' : `Negocio ${index + 1}`}</span>
        {canRemove && (
          <button type="button" className={styles.removeNegBtn} onClick={() => onRemove(index)}>
            <TrashIcon width={14} /> Quitar
          </button>
        )}
      </div>
      <div className={styles.grid}>
        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
          <label>
            ¿Tienes negocio? <span className={styles.req}>*</span>
          </label>
          <select
            value={negocio.negocio_funcionando}
            onChange={(e) => set('negocio_funcionando', e.target.value)}
          >
            <option value="">Seleccione</option>
            <option value="SI">SI</option>
            <option value="NO">NO</option>
          </select>
        </div>
        {esFuncionando && (
          <>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>
                Nombre del negocio <span className={styles.req}>*</span>
              </label>
              <input
                value={negocio.nombre_negocio}
                onChange={(e) => set('nombre_negocio', e.target.value)}
                placeholder="Ej: Tienda Natural"
              />
            </div>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>
                Sector económico <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.sector_economico}
                onChange={(e) => {
                  set('sector_economico', e.target.value);
                  onChange(index, 'tipo_emprendimiento', '');
                }}
              >
                <option value="">Seleccione</option>
                {sectoresEconomicos.map((s, idx) => {
                  const val = typeof s === 'string' ? s : s.nombre || String(s);
                  return (
                    <option key={`${val}-${idx}`} value={val}>
                      {val}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>
                Seleccione su tipo de negocio <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.tipo_emprendimiento || ''}
                onChange={(e) => set('tipo_emprendimiento', e.target.value)}
              >
                <option value="">Seleccione</option>
                {tiposNegocioFiltrados.map((t, idx) => {
                  const val = typeof t === 'string' ? t : t.nombre || String(t);
                  return (
                    <option key={`${val}-${idx}`} value={val}>
                      {val}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>
                Formalización <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.formalizacion}
                onChange={(e) => set('formalizacion', e.target.value)}
              >
                <option value="">Seleccione</option>
                {FORMAL_OPCIONES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>
                ¿Cuenta con local abierto al público o vende desde su casa? <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.tipo_local}
                onChange={(e) => set('tipo_local', e.target.value)}
              >
                <option value="">Seleccione</option>
                {TIPO_LOCAL_OPCIONES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>
                Ventas mensuales <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.ventas_mensuales}
                onChange={(e) => set('ventas_mensuales', e.target.value)}
              >
                <option value="">Seleccione</option>
                {VENTAS_OPCIONES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>
                Empleos generados <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.empleos_generados}
                onChange={(e) => set('empleos_generados', e.target.value)}
              >
                <option value="">Seleccione</option>
                {EMPLEOS_OPCIONES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>
                Años de operación <span className={styles.req}>*</span>
              </label>
              <select
                value={negocio.anios_operacion}
                onChange={(e) => set('anios_operacion', e.target.value)}
              >
                <option value="">Seleccione</option>
                {ANIOS_OPCIONES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>
                Productos o servicios <span className={styles.req}>*</span>
              </label>
              <textarea
                rows={2}
                value={negocio.productos_servicios}
                onChange={(e) => set('productos_servicios', e.target.value)}
                placeholder="Ej: Venta de ropa, comida rápida, servicios de consultoría..."
              />
            </div>
            <fieldset className={styles.fieldset}>
              <legend>Medios de pago</legend>
              <div className={styles.row}>
                {MEDIOS_PAGO.map(([key, label, IconComponent]) => {
                  const checked = Boolean(negocio.medios_pago?.[key]);
                  return (
                    <label
                      key={key}
                      className={`${styles.checkLabel} ${styles.chip} ${checked ? styles.chipActive : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => handleMedioPagoChange(key, e.target.checked)}
                      />
                      <span>
                        {IconComponent && <IconComponent width={16} className={styles.iconInline} />}
                        {label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <fieldset className={styles.fieldset}>
              <legend>
                ¿Usted comercializa sus productos por internet? (Facebook, Instagram, Whatsapp,
                Tik Tok, Pinterest, Marketplace, etc.)
              </legend>
              <div className={styles.socialList}>
                {redesSocialesCatalogo.map((red) => {
                  const checked = (negocio.redes_comercializacion || []).includes(red.nombre);
                  const clave = CLAVE_RED_POR_NOMBRE[red.nombre];
                  const Icon = clave ? ICONO_POR_CLAVE[clave] : null;
                  return (
                    <div key={red.id} className={styles.socialItem}>
                      <label className={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleComercializaToggle(red.nombre, e.target.checked)}
                        />
                        <span>
                          {Icon && <Icon width={16} className={styles.iconInline} />}
                          {red.nombre}
                        </span>
                      </label>
                      {checked && clave && (
                        <input
                          className={styles.socialUrlInput}
                          value={negocio.redes_sociales?.[clave] || ''}
                          onChange={(e) => handleRedSocialChange(clave, e.target.value)}
                          placeholder={`${PLACEHOLDER_RED[clave] || ''} (cuenta o enlace, opcional)`}
                        />
                      )}
                    </div>
                  );
                })}
                <div className={styles.socialItem}>
                  <span className={styles.otroLabel}>Otro:</span>
                  <input
                    className={styles.socialUrlInput}
                    value={otroNombre}
                    onChange={(e) => handleOtroNombreChange(e.target.value)}
                    placeholder="¿Cuál?"
                  />
                  {otroNombre && (
                    <input
                      className={styles.socialUrlInput}
                      value={negocio.redes_sociales?.otro || ''}
                      onChange={(e) => handleRedSocialChange('otro', e.target.value)}
                      placeholder="Cuenta o enlace (opcional)"
                    />
                  )}
                </div>
              </div>
            </fieldset>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>
                Fotos del negocio (máx. 5) <span className={styles.req}>*</span>
              </label>
              <p className={styles.fotoHint}>
                <PhotoIcon width={16} className={styles.iconInline} /> Toma fotos desde diferentes
                ángulos.
              </p>
              <div className={styles.fileUploadArea}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id={`fotos-neg-${index}`}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const disponibles = 5 - (negocio.fotos?.length || 0);
                    files.slice(0, disponibles).forEach((f) => onFotoAdd(index, f));
                    e.target.value = '';
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id={`fotos-neg-camara-${index}`}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const disponibles = 5 - (negocio.fotos?.length || 0);
                    files.slice(0, disponibles).forEach((f) => onFotoAdd(index, f));
                    e.target.value = '';
                  }}
                />
                <div className={styles.fileUploadButtons}>
                  <label htmlFor={`fotos-neg-${index}`} className={styles.fileUploadLabel}>
                    <PhotoIcon width={20} /> Seleccionar imágenes
                  </label>
                  <label htmlFor={`fotos-neg-camara-${index}`} className={styles.fileUploadLabel}>
                    <CameraIcon width={20} /> Tomar foto
                  </label>
                </div>
              </div>
              <div className={styles.previewContainer}>
                {(negocio.fotos || []).map((item, fi) => (
                  <div key={fi} className={styles.previewItem}>
                    <img src={item.url} alt={`Vista previa ${fi + 1}`} />
                    <button type="button" onClick={() => onFotoRemove(index, fi)}>
                      <XMarkIcon width={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const PublicRegistrationForm = () => {
  const { addToast } = useToast();
  const initialParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialEventoSlug = initialParams.get('evento');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [poblaciones, setPoblaciones] = useState([]);
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [sectoresEconomicos, setSectoresEconomicos] = useState([]);
  const [tiposNegocio, setTiposNegocio] = useState([]);
  const [nivelesEducativos, setNivelesEducativos] = useState([]);
  const [personasACargoOpciones, setPersonasACargoOpciones] = useState([]);
  const [redesSocialesCatalogo, setRedesSocialesCatalogo] = useState([]);
  const [habilitado, setHabilitado] = useState(false);
  const [mensajeBloqueo, setMensajeBloqueo] = useState(
    'El formulario publico no esta habilitado en este momento.'
  );
  const [verificando, setVerificando] = useState(true);
  const [selectedCursos, setSelectedCursos] = useState([]);
  const [evento, setEvento] = useState(null);
  const [eventoSlug, setEventoSlug] = useState(initialEventoSlug);
  const [documentoVerificado, setDocumentoVerificado] = useState(false);
  const [verificandoDocumento, setVerificandoDocumento] = useState(false);
  const [personaExistente, setPersonaExistente] = useState(null);
  const [modoInscripcionExistente, setModoInscripcionExistente] = useState(false);
  const [capacitacionServerConflict, setCapacitacionServerConflict] = useState(null);
  const [successMessage, setSuccessMessage] = useState(
    'Tu registro ha sido recibido y quedara pendiente de validacion.'
  );
  // La pantalla final (`enviado`) se reutiliza tambien para cerrar sin haber
  // mandado nada al backend (ver yaNoQuiereEmprender en
  // handleVerificarDocumento): este flag decide si el titulo/icono dicen
  // "enviado" o si solo es un aviso informativo.
  const [envioRealizado, setEnvioRealizado] = useState(true);
  // Solo se preguntan cuando ningun negocio quedo activo (ver sinNegocioActivo).
  const [quiereEmprender, setQuiereEmprender] = useState('');
  const [sectorInteres, setSectorInteres] = useState('');

  const [negocios, setNegocios] = useState([
    {
      negocio_funcionando: '',
      nombre_negocio: '',
      tipo_emprendimiento: '',
      sector_economico: '',
      productos_servicios: '',
      formalizacion: '',
      tipo_local: '',
      ventas_mensuales: '',
      empleos_generados: '',
      anios_operacion: '',
      redes_sociales: {},
      redes_comercializacion: [],
      medios_pago: {},
      fotos: [],
    },
  ]);

  // barriosNombres para validación
  const barriosNombres = useMemo(() => {
    if (!Array.isArray(barrios)) return [];
    return barrios.map((b) => b.nombre || b);
  }, [barrios]);

  const pasosBase = [
    { num: 1, title: 'Información personal' },
    { num: 2, title: 'Contacto y ubicación' },
    { num: 3, title: 'Tus negocios' },
    { num: 4, title: 'Emprendimiento' },
    { num: 5, title: 'Apoyo recibido' },
    { num: 6, title: 'Capacitaciones' },
    { num: 7, title: 'Habeas Data' },
  ];
  // Si ningun negocio quedo activo, se le pregunta a la persona si quiere
  // emprender en un paso propio (num 4), separado de "Tus negocios". Si
  // responde que no, se saltan Apoyo recibido, Capacitaciones Y Habeas Data
  // (no tiene sentido pedirle que autorice tratamiento de datos orientado a
  // "su emprendimiento" si ya nos dijo que no quiere emprender): el paso 4
  // queda como el ultimo y termina en el envio directo, con el cierre
  // cordial en vez del checkbox de habeas data.
  const sinNegocioActivo = negocios.length > 0 && negocios.every((n) => n.negocio_funcionando === 'NO');
  const noQuiereEmprender = sinNegocioActivo && quiereEmprender === 'NO';

  // El paso de Capacitaciones solo tiene sentido si el evento ofrece alguna;
  // si no hay ninguna, se omite en vez de mostrar la seccion vacia.
  const pasosActivos = pasosBase.filter((p) => {
    if (p.num === 4) return sinNegocioActivo;
    if (p.num === 6) return capacitaciones.length > 0 && !noQuiereEmprender;
    if (p.num === 5 || p.num === 7) return !noQuiereEmprender;
    return true;
  });
  const pasoActual = pasosActivos[step - 1];
  const totalPasos = pasosActivos.length;

  const schemaMap = { 1: step1Schema, 2: step2Schema, 7: step6Schema };
  const activeSchema = schemaMap[pasoActual?.num] || z.object({});
  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(activeSchema),
    defaultValues: {
      nombres: '',
      apellidos: '',
      tipo_documento: 'CC',
      numero_documento: '',
      fecha_expedicion_documento: '',
      sexo: '',
      edad: '',
      poblaciones: [],
      nivel_educativo: '',
      personas_a_cargo: '',
      telefono: '',
      tiene_whatsapp: '',
      correo: '',
      barrio: '',
      habeas_data: false,
    },
    mode: 'onChange',
  });
  const telefonoField = register('telefono');

  // nivel_educativo / personas_a_cargo cargan sus <option> desde un catalogo
  // async (a diferencia de las poblaciones, que son checkboxes que recien se
  // montan cuando el catalogo ya llego): si se restaura un borrador antes de
  // que el catalogo cargue, el navegador no encuentra la opcion y el select
  // queda en blanco. Este ref guarda el valor pendiente para reaplicarlo
  // apenas el catalogo correspondiente termine de cargar.
  const pendingRestoreRef = useRef(null);
  const draftCheckedRef = useRef(false);

  const [apoyoRecibido, setApoyoRecibido] = useState([]);
  const handleApoyoToggle = (opcion) => {
    setApoyoRecibido((prev) => {
      if (opcion === 'No he recibido apoyo') {
        return prev.includes(opcion) ? [] : ['No he recibido apoyo'];
      }
      const sinNoApoyo = prev.filter((o) => o !== 'No he recibido apoyo');
      return sinNoApoyo.includes(opcion)
        ? sinNoApoyo.filter((o) => o !== opcion)
        : [...sinNoApoyo, opcion];
    });
  };

  // Carga de evento
  useEffect(() => {
    if (initialEventoSlug) setEventoSlug(initialEventoSlug);
  }, [initialEventoSlug]);

  useEffect(() => {
    if (eventoSlug) {
      setVerificando(true);
      setHabilitado(false);
      setDocumentoVerificado(false);
      setModoInscripcionExistente(false);
      setPersonaExistente(null);
      setCapacitacionServerConflict(null);
      api
        .get(`/public/eventos/${eventoSlug}`)
        .then((res) => {
          const cursos = res.data.cursos || [];
          setEvento(res.data);
          setHabilitado(true);
          setCapacitaciones(cursos);
        })
        .catch((err) => {
          const mensaje = err.message || 'Evento no disponible o enlace invalido';
          setMensajeBloqueo(mensaje);
          addToast(mensaje, 'error');
          setHabilitado(false);
        })
        .finally(() => setVerificando(false));
    } else {
      setMensajeBloqueo(
        'El registro publico ahora se habilita desde un evento. Solicita un enlace de evento vigente.'
      );
      setHabilitado(false);
      setVerificando(false);
    }
  }, [eventoSlug, initialEventoSlug, addToast]);

  // Carga de catálogos
  useEffect(() => {
    if (habilitado && eventoSlug && !verificando) {
      Promise.allSettled([
        api.get('/catalogos/poblaciones'),
        api.get('/barrios?limit=1000'),
        api.get('/catalogos/sectores'),
        api.get('/catalogos/tipos-negocio'),
        api.get('/catalogos/niveles-educativos'),
        api.get('/catalogos/redes-sociales'),
        api.get('/catalogos/personas-a-cargo'),
      ]).then(([pob, barr, sects, tiposNeg, niveles, redes, personasCargo]) => {
        if (pob.status === 'fulfilled') {
          const pobData = pob.value.data;
          setPoblaciones(Array.isArray(pobData) ? pobData : []);
        }
        if (barr.status === 'fulfilled') {
          const barriosData = barr.value.data;
          const barriosArray = Array.isArray(barriosData) ? barriosData : barriosData?.rows || [];
          setBarrios(barriosArray);
        }
        if (sects.status === 'fulfilled') {
          const sectsData = sects.value.data;
          setSectoresEconomicos(Array.isArray(sectsData) ? sectsData : []);
        }
        if (tiposNeg.status === 'fulfilled') {
          const tiposNegData = tiposNeg.value.data;
          setTiposNegocio(Array.isArray(tiposNegData) ? tiposNegData : []);
        }
        if (niveles.status === 'fulfilled') {
          const nivelesData = niveles.value.data;
          setNivelesEducativos(Array.isArray(nivelesData) ? nivelesData : []);
        }
        if (redes.status === 'fulfilled') {
          const redesData = redes.value.data;
          setRedesSocialesCatalogo(Array.isArray(redesData) ? redesData : []);
        }
        if (personasCargo.status === 'fulfilled') {
          const personasCargoData = personasCargo.value.data;
          setPersonasACargoOpciones(Array.isArray(personasCargoData) ? personasCargoData : []);
        }
      });
    }
  }, [habilitado, eventoSlug, verificando]);

  // Recuperar un registro sin terminar (cierre accidental, boton atras,
  // pestana cerrada) para este mismo evento. Sin modal ni alerta: se
  // restaura en silencio para que el usuario simplemente siga donde iba.
  useEffect(() => {
    if (!habilitado || !eventoSlug || verificando || draftCheckedRef.current) return;
    draftCheckedRef.current = true;
    const draft = loadDraft(`registro_publico_${eventoSlug}`);
    if (!draft) return;
    pendingRestoreRef.current = {
      nivel_educativo: draft.values?.nivel_educativo,
      personas_a_cargo: draft.values?.personas_a_cargo,
    };
    reset(draft.values);
    if (draft.step) setStep(draft.step);
    if (draft.negocios) setNegocios(draft.negocios);
    if (draft.selectedCursos) setSelectedCursos(draft.selectedCursos);
    if (draft.apoyoRecibido) setApoyoRecibido(draft.apoyoRecibido);
    if (draft.quiereEmprender) setQuiereEmprender(draft.quiereEmprender);
    if (draft.sectorInteres) setSectorInteres(draft.sectorInteres);
    addToast('Continuamos tu registro donde lo dejaste', 'info');
  }, [habilitado, eventoSlug, verificando, reset, addToast]);

  useEffect(() => {
    if (nivelesEducativos.length && pendingRestoreRef.current) {
      setValue('nivel_educativo', pendingRestoreRef.current.nivel_educativo || '');
    }
  }, [nivelesEducativos, setValue]);

  useEffect(() => {
    if (personasACargoOpciones.length && pendingRestoreRef.current) {
      setValue('personas_a_cargo', pendingRestoreRef.current.personas_a_cargo || '');
    }
  }, [personasACargoOpciones, setValue]);

  // Guarda el progreso en segundo plano. `watch` con callback es una
  // suscripcion (no dispara re-render en cada tecla). No se guardan las
  // fotos de los negocios (no son serializables) ni el check de habeas data
  // (se debe volver a aceptar explicitamente).
  useEffect(() => {
    if (!eventoSlug) return undefined;
    const subscription = watch((values) => {
      saveDraft(`registro_publico_${eventoSlug}`, {
        values: { ...values, habeas_data: false },
        step,
        negocios: stripFotosNegocios(negocios),
        selectedCursos,
        apoyoRecibido,
        quiereEmprender,
        sectorInteres,
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, eventoSlug, step, negocios, selectedCursos, apoyoRecibido, quiereEmprender, sectorInteres]);

  const handleNegocioChange = (index, field, value) => {
    setNegocios((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  const handleAgregarNegocio = () => {
    if (negocios.length >= 2) {
      addToast('Máximo 2 negocios por emprendedor', 'warning');
      return;
    }
    setNegocios((prev) => [
      ...prev,
      {
        negocio_funcionando: '',
        nombre_negocio: '',
        tipo_emprendimiento: '',
        sector_economico: '',
        productos_servicios: '',
        formalizacion: '',
        tipo_local: '',
        ventas_mensuales: '',
        empleos_generados: '',
        anios_operacion: '',
        redes_sociales: {},
        redes_comercializacion: [],
        medios_pago: {},
        fotos: [],
      },
    ]);
  };
  const handleQuitarNegocio = (index) => setNegocios(prev => prev.filter((_, i) => i !== index));
  const handleCapacitacionesChange = (ids) => {
    setSelectedCursos(ids);
    setCapacitacionServerConflict(null);
  };

  const handleCapacitacionConflict = (message) => {
    addToast(message || 'La capacitacion se cruza con otro horario', 'warning');
  };

  const handleFotoAdd = (negIndex, file) => {
    setNegocios((prev) => {
      const updated = [...prev];
      if (updated[negIndex].fotos.length >= 5) return prev;
      updated[negIndex] = {
        ...updated[negIndex],
        fotos: [...updated[negIndex].fotos, { file, url: URL.createObjectURL(file) }],
      };
      return updated;
    });
  };
  const handleFotoRemove = (negIndex, fotoIndex) => {
    setNegocios((prev) => {
      const updated = [...prev];
      updated[negIndex] = {
        ...updated[negIndex],
        fotos: updated[negIndex].fotos.filter((_, i) => i !== fotoIndex),
      };
      return updated;
    });
  };

  const handleNext = async () => {
    const valid = await trigger();
    if (!valid) return;
    if (pasoActual?.num === 2) {
      const barrio = getValues('barrio');
      if (barriosNombres.length && !barriosNombres.includes(barrio)) {
        addToast('Debe seleccionar un barrio válido de la lista', 'error');
        return;
      }
    }
    if (pasoActual?.num === 3) {
      const sinSeleccion = negocios.some((n) => !n.negocio_funcionando);
      if (sinSeleccion) {
        addToast('Indique si cada negocio está funcionando', 'error');
        return;
      }
      const sinNombre = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.nombre_negocio?.trim()
      );
      if (sinNombre) {
        addToast('Ingrese el nombre de cada negocio activo', 'error');
        return;
      }
      const sinTipoNegocio = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.tipo_emprendimiento?.trim()
      );
      if (sinTipoNegocio) {
        addToast('Seleccione el tipo de negocio de cada negocio activo', 'error');
        return;
      }
      const sinSector = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.sector_economico?.trim()
      );
      if (sinSector) {
        addToast('Seleccione el sector económico de cada negocio activo', 'error');
        return;
      }
      const sinFormalizacion = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.formalizacion?.trim()
      );
      if (sinFormalizacion) {
        addToast('Seleccione la formalización de cada negocio activo', 'error');
        return;
      }
      const sinTipoLocal = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.tipo_local?.trim()
      );
      if (sinTipoLocal) {
        addToast('Indique si cada negocio activo tiene local o vende desde casa', 'error');
        return;
      }
      const sinVentas = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.ventas_mensuales?.trim()
      );
      if (sinVentas) {
        addToast('Seleccione el rango de ventas mensuales de cada negocio activo', 'error');
        return;
      }
      const sinEmpleos = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.empleos_generados?.trim()
      );
      if (sinEmpleos) {
        addToast('Seleccione el número de empleos generados por cada negocio activo', 'error');
        return;
      }
      const sinAnios = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.anios_operacion?.trim()
      );
      if (sinAnios) {
        addToast('Seleccione los años de operación de cada negocio activo', 'error');
        return;
      }
      // NUEVA VALIDACIÓN: productos o servicios
      const sinProductos = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && !n.productos_servicios?.trim()
      );
      if (sinProductos) {
        addToast('Ingrese los productos o servicios de cada negocio activo', 'error');
        return;
      }
      const sinFotos = negocios.some(
        (n) => n.negocio_funcionando === 'SI' && (n.fotos || []).length === 0
      );
      if (sinFotos) {
        addToast('Suba al menos una foto de cada negocio activo', 'error');
        return;
      }

      const redSocialSinUrl = negocios.some(
        (n) =>
          n.negocio_funcionando === 'SI' &&
          Object.entries(n.redes_sociales || {}).some(([, url]) => !String(url || '').trim())
      );
      if (redSocialSinUrl) {
        addToast(
          'Ingrese la URL o dato de contacto de cada red social seleccionada',
          'error'
        );
        return;
      }
    }
    if (pasoActual?.num === 4) {
      if (!quiereEmprender) {
        addToast('Indique si le gustaría emprender un negocio', 'error');
        return;
      }
      if (quiereEmprender === 'SI' && !sectorInteres) {
        addToast('Seleccione el sector en el que le gustaría emprender', 'error');
        return;
      }
    }
    if (pasoActual?.num === 5) {
      if (apoyoRecibido.length === 0) {
        addToast('Seleccione al menos una opción de apoyo recibido', 'error');
        return;
      }
    }
    setStep((prev) => prev + 1);
  };
  const handleBack = () => setStep((prev) => prev - 1);

  const handleVerificarDocumento = async () => {
    const numeroDocumento = String(getValues('numero_documento') || '').trim();
    if (numeroDocumento.length < 5) {
      addToast('Ingrese una cedula valida', 'error');
      return;
    }

    setVerificandoDocumento(true);
    try {
      const res = await api.post('/public/verificar-documento', {
        numero_documento: numeroDocumento,
      });
      setDocumentoVerificado(true);
      if (res.data?.existe) {
        const personaEncontrada = res.data.persona;
        const yaNoQuiereEmprender =
          personaEncontrada.quiere_emprender === 0 || personaEncontrada.quiere_emprender === false;

        if (yaNoQuiereEmprender) {
          // Ya nos dijo que no quiere emprender: no se le pide nada mas ni
          // se envia ningun registro nuevo -- solo se cierra directo con un
          // mensaje que le explica por que no puede inscribirse, sin boton
          // ni pantalla intermedia.
          setSuccessMessage(
            'Gracias por tu tiempo. Como nos contaste que por ahora no quieres emprender, este ' +
            'formulario no te permite inscribirte a capacitaciones ni eventos — está pensado para ' +
            'quienes quieren dar ese paso. Si más adelante cambias de opinión, aquí vamos a estar ' +
            'para acompañarte.'
          );
          setEnvioRealizado(false);
          setEnviado(true);
          return;
        }

        setPersonaExistente(personaEncontrada);
        setModoInscripcionExistente(true);
        setSelectedCursos([]);
        setCapacitacionServerConflict(null);
        addToast('Documento encontrado. Selecciona la capacitacion del evento.', 'success');
      } else {
        setPersonaExistente(null);
        setModoInscripcionExistente(false);
        setCapacitacionServerConflict(null);
        addToast('Documento no encontrado. Continua con el registro completo.', 'info');
      }
    } catch (err) {
      addToast(err.message || 'No se pudo verificar el documento', 'error');
    } finally {
      setVerificandoDocumento(false);
    }
  };

  const handleInscripcionExistente = async () => {
    if (!getValues('habeas_data')) {
      addToast('Debe aceptar el tratamiento de datos', 'warning');
      return;
    }

    setLoading(true);
    try {
      setCapacitacionServerConflict(null);
      await api.post(`/public/eventos/${eventoSlug}/inscripcion-existente`, {
        numero_documento: personaExistente.numero_documento,
        capacitaciones: selectedCursos,
        habeas_data: true,
      });
      if (eventoSlug) clearDraft(`registro_publico_${eventoSlug}`);
      setSuccessMessage('Tu inscripcion al evento ha sido recibida.');
      setEnviado(true);
    } catch (err) {
      if (err.details?.code === 'CAPACITACION_HORARIO_CRUCE') {
        setCapacitacionServerConflict(err.details);
      }
      addToast(err.message || 'Error al inscribirse', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    const vals = getValues();
    if (barriosNombres.length && !barriosNombres.includes(vals.barrio)) {
      addToast('Debe seleccionar un barrio válido de la lista', 'error');
      return;
    }
    // A quien no quiere emprender no se le muestra el paso de habeas data
    // (ver pasosActivos), asi que no se le puede exigir el checkbox.
    if (!noQuiereEmprender && !vals.habeas_data) {
      addToast('Debe aceptar el tratamiento de datos', 'warning');
      return;
    }
    setLoading(true);
    try {
      setCapacitacionServerConflict(null);
      const negociosActivos = negocios.filter((n) => n.negocio_funcionando === 'SI');
      const negociosPayload = negociosActivos.map((n) => ({
        negocio_funcionando: 'SI',
        nombre_negocio: n.nombre_negocio,
        tipo_emprendimiento: n.tipo_emprendimiento,
        sector_economico: n.sector_economico,
        productos_servicios: n.productos_servicios,
        formalizacion: n.formalizacion,
        tipo_local: n.tipo_local,
        ventas_mensuales: n.ventas_mensuales,
        empleos_generados: n.empleos_generados,
        anios_operacion: n.anios_operacion,
        redes_sociales: normalizeRedesSociales(n.redes_sociales),
        redes_comercializacion: n.redes_comercializacion || [],
        medios_pago: normalizeMediosPago(n.medios_pago),
      }));

      const payload = {
        tipo_documento: vals.tipo_documento,
        numero_documento: vals.numero_documento,
        fecha_expedicion_documento: vals.fecha_expedicion_documento || undefined,
        nombres: vals.nombres,
        apellidos: vals.apellidos,
        telefono: vals.telefono || undefined,
        tiene_whatsapp: vals.tiene_whatsapp || undefined,
        correo: vals.correo || undefined,
        barrio: vals.barrio || undefined,
        sexo: vals.sexo || undefined,
        edad: vals.edad ? Number(vals.edad) : undefined,
        poblaciones: (vals.poblaciones || []).map(Number),
        nivel_educativo: vals.nivel_educativo || undefined,
        personas_a_cargo: vals.personas_a_cargo || undefined,
        apoyo_recibido: apoyoRecibido.length ? apoyoRecibido : undefined,
        habeas_data: true,
        capacitaciones: selectedCursos,
        evento_slug: eventoSlug || undefined,
        negocios: negociosPayload,
        quiere_emprender: sinNegocioActivo ? quiereEmprender === 'SI' : true,
        sector_interes: sinNegocioActivo && quiereEmprender === 'SI' ? sectorInteres : undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const fd = new FormData();
      fd.append('data', JSON.stringify(payload));
      negociosActivos.forEach((neg, idx) => {
        (neg.fotos || []).forEach((f) => {
          if (f?.file) fd.append(`fotos_negocio_${idx}`, f.file);
        });
      });

      await api.post('/public/registro', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (eventoSlug) clearDraft(`registro_publico_${eventoSlug}`);
      setSuccessMessage(
        noQuiereEmprender
          ? 'Gracias por tu tiempo y por confiar en nosotros. Este formulario esta pensado para quienes quieren emprender un negocio propio, asi que por ahora no continuaremos con tu inscripcion. Si mas adelante cambias de opinion, aqui vamos a estar para acompañarte en ese paso.'
          : 'Tu registro ha sido recibido y quedara pendiente de validacion.'
      );
      setEnviado(true);
    } catch (err) {
      if (err.details?.code === 'CAPACITACION_HORARIO_CRUCE') {
        setCapacitacionServerConflict(err.details);
        setStep(pasosActivos.findIndex((paso) => paso.num === 5) + 1 || step);
      }
      addToast(err.message || 'Error al enviar', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (verificando)
    return (
      <div className={styles.page}>
        <div className={styles.container} style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Verificando disponibilidad...</p>
        </div>
      </div>
    );
  if (!habilitado)
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.disabledCard}>
            <XCircleIcon width={64} className={styles.disabledIcon} />
            <h2>Registro no disponible</h2>
            <p>{mensajeBloqueo}</p>
          </div>
        </div>
      </div>
    );
  if (enviado)
    return (
      <div className={styles.successPage}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>
            {envioRealizado ? <CheckCircleIcon width={48} /> : <InformationCircleIcon width={48} />}
          </div>
          <h1>{envioRealizado ? 'Formulario enviado' : 'No se envió ningún registro'}</h1>
          <p>{successMessage}</p>
        </div>
      </div>
    );

  if (eventoSlug && !documentoVerificado) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.header}>
            <img src="/logo.png" alt="SITRE" className={styles.logoImg} />
            <h2>SITRE - Soledad</h2>
            <p>{evento ? evento.nombre : 'Registro a evento'}</p>
          </div>
          <div className={styles.form}>
            <h3 className={styles.stepTitle}>Verifica tu cedula</h3>
            <div className={styles.grid}>
              <div className={styles.formGroup}>
                <label>Tipo doc.</label>
                <select {...register('tipo_documento')}>
                  {TIPOS_DOCUMENTO.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Numero doc. <span className={styles.req}>*</span>
                </label>
                <input {...register('numero_documento')} maxLength={15} autoFocus />
              </div>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleVerificarDocumento}
                disabled={verificandoDocumento}
              >
                {verificandoDocumento ? 'Verificando...' : 'Continuar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (modoInscripcionExistente && personaExistente) {
    // Esta pantalla nunca se muestra si la persona ya dijo que no quiere
    // emprender (ver handleVerificarDocumento, que en ese caso cierra
    // directo con el mensaje de agradecimiento sin pasar por aqui).
    return (
      <div className={styles.page}>
        <div className={`${styles.container} ${capacitaciones.length > 0 ? styles.capacitacionesContainer : ''}`}>
          <div className={styles.header}>
            <img src="/logo.png" alt="SITRE" className={styles.logoImg} />
            <h2>SITRE - Soledad</h2>
            <p>{evento ? evento.nombre : 'Registro a evento'}</p>
          </div>
          <div className={styles.form}>
            {capacitaciones.length > 0 ? (
              <>
                <h3 className={styles.stepTitle}>Inscripcion a capacitaciones</h3>
                <p>
                  Hola, {personaExistente.nombres} {personaExistente.apellidos}. Selecciona los cursos
                  disponibles para este evento.
                </p>
                <PublicCapacitacionesSelector
                  capacitaciones={capacitaciones}
                  selectedIds={selectedCursos}
                  existingCapacitaciones={personaExistente.capacitaciones || []}
                  onSelectedIdsChange={handleCapacitacionesChange}
                  onConflictAttempt={handleCapacitacionConflict}
                  serverConflict={capacitacionServerConflict}
                  helperText="Estas son las capacitaciones abiertas para este evento. El sistema bloquea horarios que se crucen con tus inscripciones actuales."
                  emptyMessage="No hay capacitaciones disponibles para este evento."
                />
              </>
            ) : (
              <>
                <h3 className={styles.stepTitle}>Confirmar inscripcion</h3>
                <p>
                  Hola, {personaExistente.nombres} {personaExistente.apellidos}. Este evento no tiene
                  capacitaciones disponibles, confirma tu inscripcion.
                </p>
              </>
            )}
            <div className={styles.habeasSection}>
              <label className={styles.checkLabel}>
                <input type="checkbox" {...register('habeas_data')} />
                <span>
                  <strong>Declaración y Protección de Datos</strong>
                  <br />
                  {HABEAS_DATA_TEXTO}
                </span>
              </label>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setDocumentoVerificado(false);
                  setModoInscripcionExistente(false);
                  setPersonaExistente(null);
                  setSelectedCursos([]);
                  setCapacitacionServerConflict(null);
                }}
              >
                Cambiar documento
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleInscripcionExistente}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar inscripcion'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.container} ${pasoActual?.num === 6 ? styles.capacitacionesContainer : ''}`}>
        <div className={styles.header}>
          <img src="/logo.png" alt="SITRE" className={styles.logoImg} />
          <h2>SITRE – Soledad</h2>
          <p>Caracterización de Emprendedores 2026 {evento ? `— ${evento.nombre}` : ''}</p>
        </div>
        <div className={styles.stepperContainer}>
          <div className={styles.stepperProgressTrack} aria-hidden="true">
            <div
              className={styles.stepperProgressFill}
              style={{ width: `${(step / totalPasos) * 100}%` }}
            />
          </div>
          <p className={styles.stepperMeta}>
            Paso {step} de {totalPasos}
          </p>
          <div className={styles.stepper}>
            {pasosActivos.map((s, idx) => (
              <div
                key={s.num}
                className={`${styles.step} ${step === idx + 1 ? styles.active : ''} ${step > idx + 1 ? styles.completed : ''}`}
              >
                <div className={styles.stepNumber}>{step > idx + 1 ? '✓' : idx + 1}</div>
                <span className={styles.stepLabel}>{s.title}</span>
                {idx < pasosActivos.length - 1 && <div className={styles.stepLine} />}
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <h3 className={styles.stepTitle}>{pasoActual?.title}</h3>

          {pasoActual?.num === 1 && (
            <div className={styles.grid}>
              <div className={styles.formGroup}>
                <label>
                  Tipo doc. <span className={styles.req}>*</span>
                </label>
                <select {...register('tipo_documento')}>
                  {TIPOS_DOCUMENTO.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Número doc. <span className={styles.req}>*</span>
                </label>
                <input {...register('numero_documento')} maxLength={15} />
                <span className={styles.error}>{errors.numero_documento?.message}</span>
              </div>
              <div className={styles.formGroup}>
                <label>Fecha expedición</label>
                <input
                  type="date"
                  max={getMaxFechaExpedicion()}
                  {...register('fecha_expedicion_documento')}
                />
                <span className={styles.error}>
                  {errors.fecha_expedicion_documento?.message}
                </span>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Nombres <span className={styles.req}>*</span>
                </label>
                <input {...register('nombres')} />
                <span className={styles.error}>{errors.nombres?.message}</span>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Apellidos <span className={styles.req}>*</span>
                </label>
                <input {...register('apellidos')} />
                <span className={styles.error}>{errors.apellidos?.message}</span>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Sexo <span className={styles.req}>*</span>
                </label>
                <select {...register('sexo')}>
                  <option value="">Seleccione</option>
                  <option>M</option>
                  <option>F</option>
                  <option>Otro</option>
                </select>
                <span className={styles.error}>{errors.sexo?.message}</span>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Edad <span className={styles.req}>*</span>
                </label>
                <input type="number" min="18" max="80" {...register('edad')} placeholder="Ej: 35" />
                <span className={styles.error}>{errors.edad?.message}</span>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>
                  Tipo de población (puede seleccionar varias) <span className={styles.req}>*</span>
                </label>
                <Controller
                  name="poblaciones"
                  control={control}
                  render={({ field }) => (
                    <div className={styles.checkGridCompact}>
                      {poblaciones.map((p) => {
                        const idStr = String(p.id);
                        const actuales = field.value || [];
                        const esNinguna = p.nombre === 'Ninguna categoría especial';
                        return (
                          <label key={p.id} className={styles.checkLabel}>
                            <input
                              type="checkbox"
                              value={p.id}
                              checked={actuales.includes(idStr)}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  field.onChange(actuales.filter((v) => v !== idStr));
                                  return;
                                }
                                if (esNinguna) {
                                  // "Ninguna categoría especial" es excluyente: anula cualquier otra seleccion.
                                  field.onChange([idStr]);
                                  return;
                                }
                                const idNinguna = poblaciones.find((x) => x.nombre === 'Ninguna categoría especial');
                                const sinNinguna = idNinguna
                                  ? actuales.filter((v) => v !== String(idNinguna.id))
                                  : actuales;
                                field.onChange([...sinNinguna, idStr]);
                              }}
                            />
                            <span>{p.nombre}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                />
                <span className={styles.error}>{errors.poblaciones?.message}</span>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>
                  ¿Cuántas personas dependen de usted o de sus ingresos? <span className={styles.req}>*</span>
                </label>
                <select {...register('personas_a_cargo')}>
                  <option value="">Seleccione</option>
                  {personasACargoOpciones.map((o) => (
                    <option key={o.id} value={o.nombre}>
                      {o.nombre}
                    </option>
                  ))}
                </select>
                <span className={styles.error}>{errors.personas_a_cargo?.message}</span>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>
                  Nivel educativo <span className={styles.req}>*</span>
                </label>
                <select {...register('nivel_educativo')}>
                  <option value="">Seleccione</option>
                  {nivelesEducativos.map((n, idx) => {
                    const val = typeof n === 'string' ? n : n.nombre || String(n);
                    return (
                      <option key={`${val}-${idx}`} value={val}>
                        {val}
                      </option>
                    );
                  })}
                </select>
                <span className={styles.error}>{errors.nivel_educativo?.message}</span>
              </div>
            </div>
          )}

          {pasoActual?.num === 2 && (
            <div className={styles.grid}>
              <div className={styles.formGroup}>
                <label>
                  Teléfono <span className={styles.req}>*</span>
                </label>
                <input
                  {...telefonoField}
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="3001234567"
                  onChange={(e) => {
                    e.target.value = sanitizePhoneInput(e.target.value);
                    telefonoField.onChange(e);
                  }}
                />
                <span className={styles.error}>{errors.telefono?.message}</span>
              </div>
              <div className={styles.formGroup}>
                <label>¿Su número de contacto tiene WhatsApp?</label>
                <select className="form-control" {...register('tiene_whatsapp')}>
                  <option value="">Seleccione</option>
                  <option value="Si">Sí</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Correo</label>
                <input type="email" {...register('correo')} />
                <span className={styles.error}>{errors.correo?.message}</span>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>
                  Barrio <span className={styles.req}>*</span>
                </label>
                <Controller
                  name="barrio"
                  control={control}
                  render={({ field }) => (
                    <BarrioSelect
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      options={barrios}
                      placeholder="Buscar y seleccionar barrio..."
                    />
                  )}
                />
                <span className={styles.error}>{errors.barrio?.message}</span>
              </div>
            </div>
          )}

          {pasoActual?.num === 3 && (
            <div>
              {negocios.map((neg, i) => (
                <NegocioForm
                  key={i}
                  index={i}
                  negocio={neg}
                  sectoresEconomicos={sectoresEconomicos}
                  tiposNegocio={tiposNegocio}
                  redesSocialesCatalogo={redesSocialesCatalogo}
                  canRemove={i > 0}
                  onChange={handleNegocioChange}
                  onRemove={handleQuitarNegocio}
                  onFotoAdd={handleFotoAdd}
                  onFotoRemove={handleFotoRemove}
                />
              ))}
              {negocios.length < 2 && (
                <button type="button" className={styles.addNegBtn} onClick={handleAgregarNegocio}>
                  <PlusCircleIcon width={18} /> Registrar un segundo negocio
                </button>
              )}
            </div>
          )}

          {pasoActual?.num === 4 && (
            <div>
              <fieldset className={styles.fieldset}>
                <legend>
                  ¿Te gustaría emprender un negocio? <span className={styles.req}>*</span>
                </legend>
                <div className={styles.row}>
                  <label
                    className={`${styles.checkLabel} ${styles.chip} ${quiereEmprender === 'SI' ? styles.chipActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="quiere_emprender"
                      checked={quiereEmprender === 'SI'}
                      onChange={() => setQuiereEmprender('SI')}
                    />
                    <span>Sí, me gustaría emprender</span>
                  </label>
                  <label
                    className={`${styles.checkLabel} ${styles.chip} ${quiereEmprender === 'NO' ? styles.chipActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="quiere_emprender"
                      checked={quiereEmprender === 'NO'}
                      onChange={() => setQuiereEmprender('NO')}
                    />
                    <span>No, por ahora no</span>
                  </label>
                </div>
                {quiereEmprender === 'SI' && (
                  <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                    <label>
                      ¿En qué sector te gustaría emprender? <span className={styles.req}>*</span>
                    </label>
                    <select value={sectorInteres} onChange={(e) => setSectorInteres(e.target.value)}>
                      <option value="">Seleccione</option>
                      {sectoresEconomicos.map((s, idx) => {
                        const val = typeof s === 'string' ? s : s.nombre || String(s);
                        return (
                          <option key={`${val}-${idx}`} value={val}>
                            {val}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </fieldset>
            </div>
          )}

          {pasoActual?.num === 5 && (
            <div>
              <fieldset className={`${styles.fieldset} ${styles.fullWidth} ${styles.apoyoFieldset}`}>
                <legend>
                  ¿Qué tipo de apoyo ha recibido del Departamento administrativo de la
                  Competitividad de la Alcaldía de Soledad? (puede seleccionar varias opciones){' '}
                  <span className={styles.req}>*</span>
                </legend>
                <div className={styles.checkGrid}>
                  {APOYO_OPCIONES.map((opcion) => {
                    const checked = apoyoRecibido.includes(opcion);
                    return (
                      <label
                        key={opcion}
                        className={`${styles.checkLabel} ${checked ? styles.chipActive : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleApoyoToggle(opcion)}
                        />
                        <span>{opcion}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          )}

          {pasoActual?.num === 6 && (
            <div>
              <PublicCapacitacionesSelector
                capacitaciones={capacitaciones}
                selectedIds={selectedCursos}
                onSelectedIdsChange={handleCapacitacionesChange}
                onConflictAttempt={handleCapacitacionConflict}
                serverConflict={capacitacionServerConflict}
                helperText="Selecciona los cursos en los que te gustaría participar (opcional)."
                emptyMessage="No hay capacitaciones disponibles en este momento."
              />
            </div>
          )}

          {pasoActual?.num === 7 && (
            <>
              <div className={styles.habeasSection}>
                <label className={styles.checkLabel}>
                  <input type="checkbox" {...register('habeas_data')} />
                  <span>
                    <strong>Declaración y Protección de Datos</strong>
                    <br />
                    {HABEAS_DATA_TEXTO}
                  </span>
                </label>
                <span className={styles.error}>{errors.habeas_data?.message}</span>
              </div>
            </>
          )}

          <div className={styles.actions}>
            {step > 1 && (
              <button type="button" className="btn btn-outline" onClick={handleBack}>
                ← Anterior
              </button>
            )}
            {step < totalPasos ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Siguiente →
              </button>
            ) : (
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar registro'}
              </button>
            )}
          </div>
        </form>
        <div className={styles.footerLogos}>
          <img src="/logo.png" alt="SITRE" />
          <img src="/alcaldia.png" alt="Alcaldía de Soledad" />
          <img src="/competitividad.png" alt="Competitividad" />
        </div>
      </div>
    </div>
  );
};

export default PublicRegistrationForm;